import { useMemo, useReducer, useEffect } from "react";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import BusinessTravel from "assets/icons/BusinessTravel.svg";
import { useSharedData } from "shared/state";
import { useMyActiveBookings } from "api/queries/bookings/myActiveBookings";
import { useCreateTravelDayBooking } from "api/mutations/booking/createTravelDayBooking";
import { OrganizationHelpers } from "shared/utils/organization";
import { filterActiveBookings } from "shared/utils/filter-booking";
import { TimeSlot, DateUtils } from "shared/utils/date";
import {
  DialogHeader,
  TimePickers,
  DatePicker,
  DialogWrapper,
  ErrorMessage,
} from "../components";
import { initialState, reducer, Actions, BusinessTripContext } from "./state";

export function BusinessTripModal() {
  const shared = useSharedData();
  const [state, dispatch] = useReducer(reducer, initialState);
  const context = useMemo(() => ({ state, dispatch, Actions }), [state]);
  const hourlyBookingFlag =
    shared.state.myOrganization?.enabledFeatures.includes("hourly_booking");
  const { t } = useTranslation();

  const minmax = useMemo<TimeSlot>(() => {
    if (shared.state.myOrganization) {
      const minmaxF = [
        shared.state.myOrganization.workingHoursStart,
        shared.state.myOrganization.workingHoursEnd,
      ].map((value) =>
        OrganizationHelpers.parseOrganizationTime(value.toString()),
      ) as TimeSlot;
      return minmaxF;
    }
    return [0, 0];
  }, [shared.state.myOrganization]);

  useEffect(() => {
    dispatch(Actions.setHoursRange(minmax));
    dispatch(Actions.selectDate(shared.state.modalInitialDate));
  }, [minmax]);

  const createTravelDayBooking = useCreateTravelDayBooking();

  const { refetch: refetchActiveBookings } = useMyActiveBookings(
    (data) => {
      const filteredByBusinessTrip = filterActiveBookings(data, "travel-day");
      dispatch(Actions.setUnavailableSlots(filteredByBusinessTrip));
    },
    {
      start: state.selectedDate,
      end: state.selectedDate,
    },
  );

  useEffect(() => {
    refetchActiveBookings().then(() => {
      shared.dispatch(shared.Actions.setLoading(false));
    });
  }, [state.selectedDate]);

  return (
    <BusinessTripContext.Provider value={context}>
      <DialogWrapper open>
        <DialogHeader
          icon={BusinessTravel}
          title={t("Home.ActiveBookings.Booking.title.travelDay") as string}
          subheader={t("Home.ActiveBooking.Booking.Popup.SubTitle")}
        />
        <DialogContent>
          <DatePicker
            caption={t("Home.ActiveBooking.Booking.BusinessTrip") as string}
            initialValue={state.selectedDate}
            onChange={(value) => {
              dispatch(Actions.selectDate(value));
            }}
          />
          {hourlyBookingFlag && (
            <TimePickers
              selectedDate={state?.selectedDate}
              hoursStart={state?.hoursStart}
              hoursEnd={state?.hoursEnd}
              onChange={(start, end) => {
                dispatch(Actions.setHoursRange([start, end]));
              }}
              setHourRange={(timeSlot) =>
                dispatch(Actions.setHoursRange(timeSlot))
              }
              unavailableSlots={state?.unavailableSlots}
              minmax={minmax}
              isDisabled={false}
            />
          )}
          {state.hoursStart === state.hoursEnd && <ErrorMessage />}
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            disabled={state.hoursStart === state.hoursEnd}
            onClick={() => {
              createTravelDayBooking({
                variables: {
                  booking: {
                    start: DateUtils.setFullUTCHour(
                      state?.selectedDate as Date,
                      state.hoursStart ?? 0,
                    ),
                    end: DateUtils.setFullUTCHour(
                      state?.selectedDate as Date,
                      state.hoursEnd ?? 0,
                    ),
                    comment: "",
                  },
                },
              })
                .then(() => {
                  shared.dispatch(shared.Actions.setLoading(true));
                  shared.dispatch(
                    shared.Actions.notifySuccess(
                      t(["Home.ActiveBooking.Booking.title"]),
                    ),
                  );
                })
                .catch((error) => {
                  shared.dispatch(
                    shared.Actions.notifyError(
                      error?.message || t(["Errors.error_title"]),
                    ),
                  );
                });
              shared.dispatch(shared.Actions.openModal());
            }}
          >
            {t(["Routes.Home.Book.Confirm"])}
          </Button>
        </DialogActions>
      </DialogWrapper>
    </BusinessTripContext.Provider>
  );
}
